import React, { useState, useEffect, useCallback } from 'react';
import { supabase, ensureSession } from '../lib/supabaseClient';
import { User, Group, GroupMember, Friend, FriendRequest, Quest, GroupQuest, GroupInvite, GroupQuestItem, Dua } from '../types';
import { ALL_QUESTS } from '../constants';
import { HeartHandshake, Users, UserPlus, UserMinus, Search, Trophy, Crown, Globe, MapPin, Loader2, Plus, Check, X, Shield, Star, Sparkles, ChevronRight, ChevronDown, Heart, Send, Trash2, LogOut, Settings, Mail, Pin, CheckCircle2, Clock, Lock, Pencil, Save } from 'lucide-react';
import QuestCard from './QuestCard';
import Leaderboard from './Leaderboard';

interface CommunityProps {
  currentUser: User;
  darkMode?: boolean;
  onCompleteGroupQuest: (quest: Quest) => void;
  onClose: () => void;
  hasFriendRequests?: boolean;
  hasGroupInvites?: boolean;
  onTrackQuest?: (quest: Quest) => void;
}

const Community: React.FC<CommunityProps> = ({ currentUser, darkMode, onCompleteGroupQuest, onClose, hasFriendRequests, hasGroupInvites, onTrackQuest }) => {
  const [tab, setTab] = useState<'duas' | 'leaderboard' | 'friends' | 'groups'>('duas');
  const [loading, setLoading] = useState(false);

  // Friends State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Friend[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [sentRequestIds, setSentRequestIds] = useState<string[]>([]);
  const [sentRequests, setSentRequests] = useState<Friend[]>([]);

  // Groups State
  const [groups, setGroups] = useState<Group[]>([]);
  const [activeGroup, setActiveGroup] = useState<Group | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupTab, setGroupTab] = useState<'leaderboard' | 'quests'>('quests');
  const [newGroupQuestTitle, setNewGroupQuestTitle] = useState('');
  const [newGroupQuestDeadline, setNewGroupQuestDeadline] = useState('');
  const [groupQuests, setGroupQuests] = useState<GroupQuestItem[]>([]);
  const [groupQuestCompletions, setGroupQuestCompletions] = useState<{ [questId: string]: number }>({});
  const [inviteFriendSearch, setInviteFriendSearch] = useState('');
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [myRole, setMyRole] = useState<'admin' | 'member'>('member');
  const [groupInvites, setGroupInvites] = useState<GroupInvite[]>([]);
  const [groupOutgoingInvites, setGroupOutgoingInvites] = useState<string[]>([]);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameText, setRenameText] = useState('');
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [declinedQuests, setDeclinedQuests] = useState<string[]>([]);
  const [hiddenQuests, setHiddenQuests] = useState<string[]>([]);

  useEffect(() => {
    const savedDeclined = localStorage.getItem('nurpath_declined_group_quests');
    if (savedDeclined) setDeclinedQuests(JSON.parse(savedDeclined));

    const savedHidden = localStorage.getItem('nurpath_hidden_group_quests');
    if (savedHidden) setHiddenQuests(JSON.parse(savedHidden));
  }, []);

  const handleDeclineQuest = (questId: string) => {
    const updated = [...declinedQuests, questId];
    setDeclinedQuests(updated);
    localStorage.setItem('nurpath_declined_group_quests', JSON.stringify(updated));
  };

  const handleHideQuest = (questId: string) => {
    const updated = [...hiddenQuests, questId];
    setHiddenQuests(updated);
    localStorage.setItem('nurpath_hidden_group_quests', JSON.stringify(updated));
  };

  // Duas State
  const [duas, setDuas] = useState<Dua[]>([]);
  const [newDuaText, setNewDuaText] = useState('');
  const [duaLoading, setDuaLoading] = useState(false);
  const [myDuaCountToday, setMyDuaCountToday] = useState(0);

  useEffect(() => {
    const loadTab = async () => {
      // Force token refresh if expired (fixes issue where tab goes to sleep)
      await ensureSession();
      if (tab === 'friends') fetchFriendsAndRequests();
      else if (tab === 'groups') { fetchGroups(); fetchGroupInvites(); }
      else if (tab === 'duas') fetchDuas();
    };
    loadTab();
  }, [tab, currentUser.id]);

  useEffect(() => {
    if (activeGroup) {
      const fetchOutgoing = async () => {
        const { data } = await supabase.from('group_invites').select('invited_user').eq('group_id', activeGroup.id).eq('status', 'pending');
        setGroupOutgoingInvites(data?.map(i => i.invited_user) || []);
      };
      fetchOutgoing();
    } else {
      setGroupOutgoingInvites([]);
    }
  }, [activeGroup?.id]);

  // ==================== DUAS LOGIC ====================

  const fetchDuas = async () => {
    // Load from cache first to prevent blank screen
    const cachedDuas = localStorage.getItem('nurpath_cached_duas');
    if (cachedDuas && duas.length === 0) {
      try { setDuas(JSON.parse(cachedDuas)); } catch (e) { }
    }

    setDuaLoading(true);
    try {
      // Only fetch duas from last 60 days
      const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
      const { data: duasData } = await supabase
        .from('duas')
        .select('*')
        .gte('created_at', sixtyDaysAgo)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!duasData) { setDuas([]); return; }

      // Get usernames
      const userIds = [...new Set(duasData.map(d => d.user_id))];
      const { data: profiles } = await supabase.from('profiles').select('id, username').in('id', userIds);

      // Check which ones I said ameen to
      const { data: myAmeens } = await supabase
        .from('dua_ameens')
        .select('dua_id')
        .eq('user_id', currentUser.id);
      const myAmeenIds = new Set(myAmeens?.map(a => a.dua_id) || []);

      const enriched: Dua[] = duasData.map(d => ({
        ...d,
        username: profiles?.find(p => p.id === d.user_id)?.username || 'Unknown',
        has_said_ameen: myAmeenIds.has(d.id)
      }));

      setDuas(enriched);
      // Cache the result
      localStorage.setItem('nurpath_cached_duas', JSON.stringify(enriched));

      // Count my duas in last 24h for rate limiting
      const twentyFourAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { count: myCount } = await supabase
        .from('duas')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', currentUser.id)
        .gte('created_at', twentyFourAgo);
      setMyDuaCountToday(myCount || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setDuaLoading(false);
    }
  };

  const postDua = async () => {
    if (!newDuaText.trim() || newDuaText.length > 500) return;
    if (myDuaCountToday >= 2) {
      alert('You can only post 2 duas every 24 hours.');
      return;
    }
    try {
      const { error } = await supabase.from('duas').insert({ user_id: currentUser.id, text: newDuaText.trim() });
      if (error) throw error;
      setNewDuaText('');
      setMyDuaCountToday(prev => prev + 1);
      fetchDuas();
    } catch (e) {
      console.error(e);
    }
  };

  const sayAmeen = async (duaId: string) => {
    // 1. Optimistic UI Update: Instantly show the ameen as clicked
    setDuas(prev => prev.map(d =>
      d.id === duaId
        ? { ...d, ameen_count: d.ameen_count + 1, has_said_ameen: true }
        : d
    ));

    try {
      // 2. Background sync
      const { error } = await supabase.from('dua_ameens').insert({ dua_id: duaId, user_id: currentUser.id });
      if (error) {
        // If it's just a duplicate constraint error, we can ignore because it means they already liked it
        if (error.code !== '23505') throw error;
      }
    } catch (e) {
      console.error('Failed to save ameen:', e);
      // 3. Rollback if it actually failed
      setDuas(prev => prev.map(d =>
        d.id === duaId
          ? { ...d, ameen_count: Math.max(0, d.ameen_count - 1), has_said_ameen: false }
          : d
      ));
    }
  };

  const deleteDua = async (duaId: string) => {
    try {
      await supabase.from('duas').delete().eq('id', duaId);
      setDuas(prev => prev.filter(d => d.id !== duaId));
    } catch (e) {
      console.error(e);
    }
  };

  // ==================== FRIEND LOGIC ====================

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, xp, country')
        .ilike('username', `%${searchQuery}%`)
        .neq('id', currentUser.id)
        .limit(5);
      setSearchResults(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const sendFriendRequest = async (targetId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .insert({ user_id_1: currentUser.id, user_id_2: targetId });
      if (error) throw error;
      setSentRequestIds(prev => [...prev, targetId]);
      alert('Request sent!');
      setSearchResults(prev => prev.filter(p => p.id !== targetId));
    } catch (e: any) {
      alert('Error sending request (maybe already sent?)');
    }
  };

  const cancelFriendRequest = async (targetId: string) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('user_id_1', currentUser.id)
        .eq('user_id_2', targetId)
        .eq('status', 'pending');

      if (error) throw error;

      // Optimistically remove from state
      setSentRequestIds(prev => prev.filter(id => id !== targetId));
      setSentRequests(prev => prev.filter(s => s.id !== targetId));
    } catch (e: any) {
      alert('Error canceling request');
    } finally {
      setLoading(false);
    }
  };

  const fetchFriendsAndRequests = async () => {
    setLoading(true);
    try {
      const { data: reqs } = await supabase
        .from('friendships')
        .select('id, sender_id:user_id_1, status, created_at')
        .eq('user_id_2', currentUser.id)
        .eq('status', 'pending');

      if (reqs && reqs.length > 0) {
        const senderIds = reqs.map(r => r.sender_id);
        const { data: profiles } = await supabase.from('profiles').select('id, username, xp, country').in('id', senderIds);
        const enrichedReqs = reqs.map(r => ({
          ...r,
          sender: profiles?.find(p => p.id === r.sender_id)
        })) as unknown as FriendRequest[];
        setRequests(enrichedReqs);
      } else {
        setRequests([]);
      }

      const { data: friendData } = await supabase
        .from('friendships')
        .select('user_id_1, user_id_2')
        .or(`user_id_1.eq.${currentUser.id},user_id_2.eq.${currentUser.id}`)
        .eq('status', 'accepted');

      const friendIds = friendData?.map(f => f.user_id_1 === currentUser.id ? f.user_id_2 : f.user_id_1) || [];

      if (friendIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, xp, country, active_quests')
          .in('id', friendIds);
        setFriends(profiles || []);
      } else {
        setFriends([]);
      }

      // Fetch outgoing pending requests
      const { data: sent } = await supabase
        .from('friendships')
        .select('user_id_2')
        .eq('user_id_1', currentUser.id)
        .eq('status', 'pending');
      const sentIds = sent?.map(s => s.user_id_2) || [];
      setSentRequestIds(sentIds);
      if (sentIds.length > 0) {
        const { data: sentProfiles } = await supabase
          .from('profiles')
          .select('id, username, xp, country, active_quests')
          .in('id', sentIds);
        setSentRequests(sentProfiles || []);
      } else {
        setSentRequests([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const respondToRequest = async (reqId: string, accept: boolean) => {
    try {
      if (accept) {
        await supabase.from('friendships').update({ status: 'accepted' }).eq('id', reqId);
      } else {
        await supabase.from('friendships').delete().eq('id', reqId);
      }
      fetchFriendsAndRequests();
    } catch (e) {
      console.error(e);
    }
  };

  // ==================== GROUP LOGIC ====================

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const { data: group, error } = await supabase
        .from('groups')
        .insert({ name: newGroupName, created_by: currentUser.id })
        .select()
        .single();

      if (error) throw error;

      // Add self as admin
      await supabase.from('group_members').insert({ group_id: group.id, user_id: currentUser.id, role: 'admin' });

      setGroups(prev => [...prev, group]);
      setNewGroupName('');
      setShowCreateGroup(false);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const { data: members } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', currentUser.id);

      const groupIds = members?.map(m => m.group_id) || [];

      if (groupIds.length > 0) {
        const { data: groupsData } = await supabase.from('groups').select('*').in('id', groupIds);
        setGroups(groupsData || []);
      } else {
        setGroups([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupInvites = async () => {
    try {
      const { data: invites } = await supabase
        .from('group_invites')
        .select('*')
        .eq('invited_user', currentUser.id)
        .eq('status', 'pending');

      if (!invites || invites.length === 0) { setGroupInvites([]); return; }

      // Get group names and inviter names
      const groupIds = [...new Set(invites.map(i => i.group_id))];
      const inviterIds = [...new Set(invites.map(i => i.invited_by))];

      const [{ data: groupsData }, { data: profiles }] = await Promise.all([
        supabase.from('groups').select('id, name').in('id', groupIds),
        supabase.from('profiles').select('id, username').in('id', inviterIds)
      ]);

      const enriched: GroupInvite[] = invites.map(i => ({
        ...i,
        group_name: groupsData?.find(g => g.id === i.group_id)?.name || 'Unknown',
        inviter_name: profiles?.find(p => p.id === i.invited_by)?.username || 'Unknown'
      }));
      setGroupInvites(enriched);
    } catch (e) {
      console.error(e);
    }
  };

  const respondToGroupInvite = async (inviteId: string, groupId: string, accept: boolean) => {
    try {
      if (accept) {
        await supabase.from('group_invites').update({ status: 'accepted' }).eq('id', inviteId);
        await supabase.from('group_members').insert({ group_id: groupId, user_id: currentUser.id, role: 'member' });
      } else {
        await supabase.from('group_invites').update({ status: 'declined' }).eq('id', inviteId);
      }
      fetchGroupInvites();
      fetchGroups();
    } catch (e) {
      console.error(e);
    }
  };

  const sendGroupInvite = async (friendId: string) => {
    if (!activeGroup) return;
    try {
      const { error } = await supabase.from('group_invites').insert({
        group_id: activeGroup.id,
        invited_user: friendId,
        invited_by: currentUser.id
      });
      if (error) {
        if (error.code === '23505') alert('Already invited!');
        else throw error;
      } else {
        alert('Invite sent!');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to send invite.');
    }
  };

  const openGroup = async (group: Group) => {
    setLoading(true);
    try {
      const { data: members } = await supabase
        .from('group_members')
        .select('user_id, role')
        .eq('group_id', group.id);

      const userIds = members?.map(m => m.user_id) || [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, xp, country, active_quests')
        .in('id', userIds);

      const membersWithRole: GroupMember[] = (profiles || []).map(p => ({
        ...p,
        role: members?.find(m => m.user_id === p.id)?.role || 'member'
      }));

      setActiveGroup({ ...group, members: membersWithRole });

      // Determine my role
      const me = members?.find(m => m.user_id === currentUser.id);
      setMyRole(me?.role || 'member');

      // Fetch group custom quests
      const { data: quests } = await supabase
        .from('group_quests')
        .select('*')
        .eq('group_id', group.id)
        .order('created_at', { ascending: false });

      if (quests && quests.length > 0) {
        setGroupQuests(quests);
        // Fetch completions for these quests
        const qIds = quests.map(q => q.id);
        const { data: completions } = await supabase.from('group_quest_completions').select('group_quest_id').in('group_quest_id', qIds);

        const counts: { [id: string]: number } = {};
        if (completions) {
          completions.forEach(c => {
            counts[c.group_quest_id] = (counts[c.group_quest_id] || 0) + 1;
          });
        }
        setGroupQuestCompletions(counts);
      } else {
        setGroupQuests([]);
        setGroupQuestCompletions({});
      }

      setGroupTab('quests');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };


  const renameGroup = async () => {
    if (!activeGroup || !renameText.trim()) return;
    try {
      const { data, error } = await supabase.from('groups').update({ name: renameText.trim() }).eq('id', activeGroup.id).select();
      if (error) throw error;

      if (!data || data.length === 0) {
        alert('Permission denied by Database. Please run the provided SQL migration in Supabase to allow Admins to rename groups.');
        return;
      }

      const newName = renameText.trim();
      setActiveGroup(prev => prev ? { ...prev, name: newName } : null);
      setGroups(prev => prev.map(g => g.id === activeGroup.id ? { ...g, name: newName } : g));
      setIsRenaming(false);
    } catch (e) {
      console.error(e);
      alert('Failed to rename group');
    }
  };

  const addGroupQuest = async () => {
    if (!newGroupQuestTitle.trim() || !activeGroup) return;
    if ((activeGroup.members?.length || 0) < 2) {
      alert('You need at least 2 group members to create a challenge.');
      return;
    }
    try {
      const { error } = await supabase.from('group_quests').insert({
        group_id: activeGroup.id,
        title: newGroupQuestTitle.trim(),
        created_by: currentUser.id,
        xp: 50,
        deadline: newGroupQuestDeadline || null
      });

      if (error) throw error;
      setNewGroupQuestTitle('');
      setNewGroupQuestDeadline('');

      // Refresh quests
      const { data: quests } = await supabase
        .from('group_quests')
        .select('*')
        .eq('group_id', activeGroup.id)
        .order('created_at', { ascending: false });

      setGroupQuests(quests || []);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteGroupQuest = async (questId: string) => {
    try {
      await supabase.from('group_quests').delete().eq('id', questId);
      setGroupQuests(prev => prev.filter(q => q.id !== questId));
    } catch (e) {
      console.error(e);
    }
  };

  const promoteMember = async (memberId: string) => {
    if (!activeGroup) return;
    try {
      await supabase.from('group_members').update({ role: 'admin' }).eq('group_id', activeGroup.id).eq('user_id', memberId);
      openGroup(activeGroup);
    } catch (e) {
      console.error(e);
    }
  };

  const removeMember = async (memberId: string) => {
    if (!activeGroup) return;
    try {
      await supabase.from('group_members').delete().eq('group_id', activeGroup.id).eq('user_id', memberId);
      openGroup(activeGroup);
    } catch (e) {
      console.error(e);
    }
  };

  const leaveGroup = async () => {
    if (!activeGroup) return;
    if (!confirm('Are you sure you want to leave this group?')) return;
    try {
      await supabase.from('group_members').delete().eq('group_id', activeGroup.id).eq('user_id', currentUser.id);
      setActiveGroup(null);
      fetchGroups();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteGroup = async () => {
    if (!activeGroup || !confirm('Are you sure you want to delete this group? You can\'t undo this.')) return;
    try {
      await supabase.from('groups').delete().eq('id', activeGroup.id);
      setActiveGroup(null);
      fetchGroups();
    } catch (e) {
      console.error(e);
    }
  };

  const makeGroupAdmin = async (userId: string) => {
    if (!activeGroup || myRole !== 'admin') return;
    if (!confirm('Make this user an admin?')) return;
    try {
      const { data, error } = await supabase.from('group_members').update({ role: 'admin' }).eq('group_id', activeGroup.id).eq('user_id', userId).select();
      if (error) throw error;
      if (!data || data.length === 0) return alert('Permission denied! Please run the SQL migration for group_members UPDATE.');
      setActiveGroup(prev => {
        if (!prev) return null;
        return { ...prev, members: prev.members?.map(m => m.id === userId ? { ...m, role: 'admin' } : m) };
      });
    } catch (e) { console.error(e); alert('Failed to make admin'); }
  };

  const removeGroupMember = async (userId: string) => {
    if (!activeGroup || myRole !== 'admin') return;
    if (!confirm('Remove this user from the group?')) return;
    try {
      const { data, error } = await supabase.from('group_members').delete().eq('group_id', activeGroup.id).eq('user_id', userId).select();
      if (error) throw error;
      if (!data || data.length === 0) return alert('Permission denied! Please run the SQL migration for group_members DELETE.');
      setActiveGroup(prev => {
        if (!prev) return null;
        return { ...prev, members: prev.members?.filter(m => m.id !== userId) };
      });
    } catch (e) { console.error(e); alert('Failed to remove member'); }
  };

  // Check if a quest was completed today
  const isQuestCompletedToday = (questId: string): boolean => {
    const todayStr = new Date().toISOString().split('T')[0];
    return currentUser.completedDailyQuests?.[questId] === todayStr;
  };

  // Filtered groups for search
  const filteredGroups = groups.filter(g =>
    g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())
  );

  const filteredActiveMembers = activeGroup?.members?.filter(m =>
    m.username.toLowerCase().includes(memberSearchQuery.toLowerCase())
  ) || [];

  // Friends not in active group (for invite)
  const invitableFriends = friends.filter(f => {
    if (!activeGroup?.members) return true;
    return !activeGroup.members.some(m => m.id === f.id);
  }).filter(f => f.username.toLowerCase().includes(inviteFriendSearch.toLowerCase()));

  // Time ago helper
  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className={`h-full flex flex-col ${darkMode ? 'bg-[#0a0a0a]' : 'bg-white'} overflow-hidden`}>

      {/* ─── MINIMAL HEADER ─── */}
      <div className={`shrink-0 px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-0 ${darkMode ? '' : ''}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className={`text-2xl font-black tracking-[-0.03em] ${darkMode ? 'text-white' : 'text-[#111]'}`}>Nur-Connect</h2>
            <p className={`text-[10px] font-semibold uppercase tracking-[0.25em] mt-0.5 ${darkMode ? 'text-white/25' : 'text-slate-400'}`}>Community</p>
          </div>
          <button onClick={onClose} className={`w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-90 ${darkMode ? 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600'}`}>
            <X size={16} />
          </button>
        </div>

        {/* ─── TAB BAR (underline style) ─── */}
        <div className={`flex border-b ${darkMode ? 'border-white/5' : 'border-slate-100'}`}>
          {([
            { key: 'duas', label: 'Duas', badge: false },
            { key: 'friends', label: 'Friends', badge: hasFriendRequests },
            { key: 'groups', label: 'Groups', badge: hasGroupInvites },
            { key: 'leaderboard', label: 'Ranks', badge: false },
          ] as const).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key as any)}
              className={`relative flex-1 pb-3 text-[11px] font-bold uppercase tracking-[0.12em] transition-all duration-300 ${tab === t.key
                ? darkMode ? 'text-white' : 'text-[#111]'
                : darkMode ? 'text-white/25 hover:text-white/50' : 'text-slate-300 hover:text-slate-500'
                }`}
            >
              {t.label}
              {tab === t.key && (
                <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-[2px] rounded-full ${darkMode ? 'bg-white' : 'bg-[#111]'}`} />
              )}
              {t.badge && (
                <span className="absolute top-0 right-2 w-1.5 h-1.5 bg-rose-500 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <div className="flex-1 overflow-y-auto px-6 pt-6 pb-[max(6rem,env(safe-area-inset-bottom))] scrollbar-hide">

        {/* ═══════════════ DUAS TAB ═══════════════ */}
        {tab === 'duas' && (
          <div className="space-y-5 animate-in fade-in duration-500">

            {/* POST DUA */}
            <div className={`mb-6 pb-2 ${darkMode ? 'border-b border-white/[0.08]' : 'border-b border-slate-200'}`}>
              <textarea
                placeholder="Share a dua..."
                className={`w-full bg-transparent text-[15px] leading-relaxed resize-none outline-none min-h-[50px] ${darkMode ? 'text-white/90 placeholder:text-white/25' : 'text-slate-800 placeholder:text-slate-400'}`}
                value={newDuaText}
                onChange={e => setNewDuaText(e.target.value)}
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-2">
                <span className={`text-[10px] font-medium tabular-nums ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>{newDuaText.length}/500</span>
                <button
                  onClick={postDua}
                  disabled={!newDuaText.trim()}
                  className={`px-5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] transition-all active:scale-95 disabled:opacity-20 ${darkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-[#111] text-white hover:bg-black'}`}
                >
                  Post
                </button>
              </div>
            </div>

            {/* DUA FEED */}
            {duaLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className={`animate-spin ${darkMode ? 'text-white/20' : 'text-slate-200'}`} size={24} />
              </div>
            ) : duas.length === 0 ? (
              <div className="text-center py-20">
                <p className={`text-sm ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>No duas yet. Be the first.</p>
              </div>
            ) : (
              duas.map((dua, i) => (
                <div
                  key={dua.id}
                  className={`group py-5 transition-all duration-300 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}
                >
                  {/* Author line */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}>
                        {dua.username?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <span className={`text-[12px] font-semibold ${darkMode ? 'text-white/70' : 'text-slate-700'}`}>{dua.username}</span>
                        <span className={`text-[10px] ml-2 ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>{timeAgo(dua.created_at)}</span>
                      </div>
                    </div>
                    {dua.user_id === currentUser.id && (
                      <button onClick={() => deleteDua(dua.id)} className={`p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all ${darkMode ? 'text-white/15 hover:text-rose-400 hover:bg-white/5' : 'text-slate-300 hover:text-rose-500 hover:bg-slate-50'}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>

                  {/* Dua text */}
                  <p className={`text-[14px] leading-[1.7] ${darkMode ? 'text-white/60' : 'text-slate-600'}`}>{dua.text}</p>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={() => !dua.has_said_ameen && sayAmeen(dua.id)}
                      disabled={dua.has_said_ameen || dua.user_id === currentUser.id}
                      className={`text-[11px] font-semibold transition-all active:scale-95 ${dua.has_said_ameen
                        ? darkMode ? 'text-[#d4af37]/50' : 'text-[#b8960b]/50'
                        : dua.user_id === currentUser.id
                          ? darkMode ? 'text-white/10 cursor-default' : 'text-slate-200 cursor-default'
                          : darkMode ? 'text-[#d4af37]/40 hover:text-[#d4af37]/80' : 'text-[#b8960b]/50 hover:text-[#b8960b]'
                        }`}
                    >
                      {dua.has_said_ameen ? '✓ Ameen' : 'Ameen'}
                    </button>
                    <span className={`text-[10px] tabular-nums ${darkMode ? 'text-white/10' : 'text-slate-200'}`}>
                      {dua.ameen_count}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ==================== LEADERBOARD TAB ==================== */}
        {tab === 'leaderboard' && (
          <Leaderboard
            currentUserId={currentUser.id!}
            currentUserCountry={currentUser.country}
            onClose={() => { }}
            darkMode={darkMode}
            embedded={true}
            leaderboardEnabled={currentUser.settings?.leaderboardEnabled}
          />
        )}

        {/* ═══════════════ FRIENDS TAB ═══════════════ */}
        {tab === 'friends' && (
          <div className="space-y-6 animate-in fade-in duration-500">
            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${darkMode ? 'text-white/15' : 'text-slate-300'}`} size={15} />
              <input
                type="text"
                placeholder="Search by username..."
                className={`w-full p-3.5 pl-10 rounded-xl text-[13px] outline-none transition-all ${darkMode ? 'bg-white/[0.03] text-white/80 border border-white/[0.06] placeholder:text-white/15 focus:border-white/15' : 'bg-slate-50/80 text-slate-700 border border-slate-100 placeholder:text-slate-300 focus:border-slate-200'}`}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchUsers()}
              />
              <button onClick={searchUsers} className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-[#111] text-white hover:bg-black'}`}>
                {loading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
              </button>
            </div>

            {/* Results */}
            {searchResults.length > 0 && (
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 ${darkMode ? 'text-white/20' : 'text-slate-400'}`}>Results</p>
                {searchResults.map((res, i) => (
                  <div key={res.id} className={`flex justify-between items-center py-3.5 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}>{res.username[0].toUpperCase()}</div>
                      <div>
                        <p className={`text-[13px] font-semibold ${darkMode ? 'text-white/80' : 'text-slate-700'}`}>{res.username}</p>
                        <p className={`text-[10px] ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>{res.country}</p>
                      </div>
                    </div>
                    {(() => {
                      const isFriend = friends.some(f => f.id === res.id);
                      const isPendingOutgoing = sentRequestIds.includes(res.id);
                      const isPendingIncoming = requests.some(r => r.sender?.id === res.id);
                      if (isFriend) return <span className={`text-[9px] font-bold uppercase tracking-widest ${darkMode ? 'text-white/20' : 'text-slate-300'}`}>Added</span>;
                      if (isPendingIncoming) return <span className={`text-[9px] font-bold uppercase tracking-widest ${darkMode ? 'text-[#d4af37]/40' : 'text-[#b8960b]/50'}`}>Pending</span>;
                      if (isPendingOutgoing) return (
                        <button onClick={() => cancelFriendRequest(res.id)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 hover:text-rose-300' : 'bg-rose-50 text-rose-500 hover:bg-rose-100 hover:text-rose-600'} group`} title="Cancel sent request">
                          <UserMinus size={14} />
                        </button>
                      );
                      return (
                        <button onClick={() => sendFriendRequest(res.id)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white/[0.06] text-white/40 hover:bg-white/10 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-[#111] hover:text-white'}`}>
                          <UserPlus size={14} />
                        </button>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}

            {/* Requests */}
            {requests.length > 0 && (
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 flex items-center gap-2 ${darkMode ? 'text-white/20' : 'text-slate-400'}`}>
                  <span className="w-1.5 h-1.5 bg-rose-500 rounded-full" /> Requests
                </p>
                {requests.map((req, i) => (
                  <div key={req.id} className={`flex justify-between items-center py-3.5 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}>{req.sender?.username[0].toUpperCase()}</div>
                      <div>
                        <p className={`text-[13px] font-semibold ${darkMode ? 'text-white/80' : 'text-slate-700'}`}>{req.sender?.username}</p>
                        <p className={`text-[10px] ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>Wants to connect</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => respondToRequest(req.id, true)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white text-black' : 'bg-[#111] text-white'}`}><Check size={13} /></button>
                      <button onClick={() => respondToRequest(req.id, false)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white/[0.06] text-white/40 hover:text-rose-400' : 'bg-slate-100 text-slate-400 hover:text-rose-500'}`}><X size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Friends List */}
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 ${darkMode ? 'text-white/20' : 'text-slate-400'}`}>Your Circle</p>
              {friends.length === 0 ? (
                <p className={`text-[13px] py-8 text-center ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>No connections yet.</p>
              ) : (
                friends.map((f, i) => (
                  <div key={f.id} className={`flex items-center gap-2.5 py-3.5 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}>{f.username[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] font-semibold ${darkMode ? 'text-white/80' : 'text-slate-700'}`}>{f.username}</p>
                      <p className={`text-[10px] ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>{f.xp.toLocaleString()} XP · {f.country}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Sent Requests */}
            <div className="pt-6">
              <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 ${darkMode ? 'text-white/20' : 'text-slate-400'}`}>Sent Requests</p>
              {sentRequests.length === 0 ? (
                <p className={`text-[13px] py-4 text-center ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>None</p>
              ) : (
                sentRequests.map((s, i) => (
                  <div key={s.id} className={`flex items-center justify-between py-3.5 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}>{s.username[0].toUpperCase()}</div>
                      <div>
                        <p className={`text-[13px] font-semibold ${darkMode ? 'text-white/80' : 'text-slate-700'}`}>{s.username}</p>
                        <p className={`text-[10px] ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>Waiting for response</p>
                      </div>
                    </div>
                    <button onClick={() => cancelFriendRequest(s.id)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 hover:text-rose-300' : 'bg-rose-50 text-rose-500 hover:bg-rose-100 hover:text-rose-600'} group`} title="Cancel sent request">
                      <UserMinus size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ═══════════════ GROUPS TAB ═══════════════ */}
        {tab === 'groups' && !activeGroup && (
          <div className="space-y-6 animate-in fade-in duration-500">
            {/* Invites */}
            {groupInvites.length > 0 && (
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 flex items-center gap-2 ${darkMode ? 'text-white/20' : 'text-slate-400'}`}>
                  <span className="w-1.5 h-1.5 bg-rose-500 rounded-full" /> Group Invites
                </p>
                {groupInvites.map((inv, i) => (
                  <div key={inv.id} className={`flex justify-between items-center py-3.5 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}>
                    <div>
                      <p className={`text-[13px] font-semibold ${darkMode ? 'text-white/80' : 'text-slate-700'}`}>{inv.group_name}</p>
                      <p className={`text-[10px] ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>from {inv.inviter_name}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => respondToGroupInvite(inv.id, inv.group_id, true)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white text-black' : 'bg-[#111] text-white'}`}><Check size={13} /></button>
                      <button onClick={() => respondToGroupInvite(inv.id, inv.group_id, false)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}><X size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Create */}
            <button
              onClick={() => setShowCreateGroup(!showCreateGroup)}
              className={`w-full py-3.5 rounded-xl text-[11px] font-bold uppercase tracking-[0.12em] transition-all active:scale-[0.98] ${darkMode ? 'border border-dashed border-white/[0.08] text-white/25 hover:text-white/50 hover:border-white/15' : 'border border-dashed border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300'}`}
            >
              + New Group
            </button>

            {showCreateGroup && (
              <div className={`p-4 rounded-xl space-y-3 ${darkMode ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-slate-50/80 border border-slate-100'}`}>
                <input
                  type="text"
                  placeholder="Group name"
                  className={`w-full p-3.5 rounded-xl text-[13px] outline-none transition-all ${darkMode ? 'bg-white/[0.04] text-white/80 border border-white/[0.04] placeholder:text-white/15 focus:border-white/10' : 'bg-white text-slate-700 border border-slate-200/60 placeholder:text-slate-300 focus:border-slate-300'}`}
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                />
                <button onClick={createGroup} className={`w-full py-3 rounded-lg text-[10px] font-bold uppercase tracking-[0.1em] transition-all active:scale-[0.98] ${darkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-[#111] text-white hover:bg-black'}`}>Create</button>
              </div>
            )}

            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${darkMode ? 'text-white/15' : 'text-slate-300'}`} size={14} />
              <input
                type="text"
                placeholder="Search groups..."
                className={`w-full p-3 pl-10 rounded-xl text-[13px] outline-none transition-all ${darkMode ? 'bg-white/[0.03] text-white/80 border border-white/[0.06] placeholder:text-white/15 focus:border-white/15' : 'bg-slate-50/80 text-slate-700 border border-slate-100 placeholder:text-slate-300 focus:border-slate-200'}`}
                value={groupSearchQuery}
                onChange={e => setGroupSearchQuery(e.target.value)}
              />
            </div>

            {/* List */}
            <div>
              {filteredGroups.length === 0 ? (
                <p className={`text-[13px] py-10 text-center ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>{groupSearchQuery ? 'No matches.' : 'No groups yet.'}</p>
              ) : (
                filteredGroups.map((g, i) => (
                  <div
                    key={g.id}
                    onClick={() => openGroup(g)}
                    className={`group flex items-center justify-between py-4 cursor-pointer transition-all ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${darkMode ? 'bg-white/[0.04] text-white/25' : 'bg-slate-100 text-slate-400'}`}>
                        <Shield size={16} />
                      </div>
                      <div>
                        <p className={`text-[13px] font-semibold ${darkMode ? 'text-white/80' : 'text-slate-700'}`}>{g.name}</p>
                      </div>
                    </div>
                    <ChevronRight className={`transition-all group-hover:translate-x-0.5 ${darkMode ? 'text-white/10' : 'text-slate-200'}`} size={16} />
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ==================== ACTIVE GROUP VIEW ==================== */}
        {tab === 'groups' && activeGroup && (
          <div className="space-y-6 animate-in zoom-in-95 duration-500">
            {/* Back Button */}
            <button onClick={() => { setActiveGroup(null); setShowInvitePanel(false); }} className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] transition-all ${darkMode ? 'text-white/25 hover:text-white/50' : 'text-slate-400 hover:text-slate-600'}`}>
              <ChevronRight className="rotate-180" size={11} /> Back
            </button>

            {/* Group Header */}
            <div className="text-center space-y-4">
              {isRenaming ? (
                <div className="flex items-center justify-center gap-2">
                  <input
                    value={renameText}
                    onChange={e => setRenameText(e.target.value)}
                    className={`text-lg font-bold text-center bg-transparent border-b outline-none w-2/3 ${darkMode ? 'text-white border-white/10' : 'text-slate-900 border-slate-200'}`}
                    autoFocus
                  />
                  <button onClick={renameGroup} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white text-black' : 'bg-[#111] text-white'}`}><Save size={13} /></button>
                  <button onClick={() => setIsRenaming(false)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}><X size={13} /></button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 group/title">
                  <h2 className={`text-xl font-black tracking-[-0.02em] ${darkMode ? 'text-white' : 'text-[#111]'}`}>{activeGroup.name}</h2>
                  {myRole === 'admin' && (
                    <button
                      onClick={() => { setRenameText(activeGroup.name); setIsRenaming(true); }}
                      className={`opacity-0 group-hover/title:opacity-100 transition-all p-1 rounded-md ${darkMode ? 'text-white/20 hover:text-white/50' : 'text-slate-300 hover:text-slate-500'}`}
                    >
                      <Pencil size={11} />
                    </button>
                  )}
                </div>
              )}
              <div className="flex justify-center -space-x-1.5">
                {activeGroup.members?.map((m, i) => (
                  <div
                    key={i}
                    className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[9px] font-bold ${darkMode ? 'bg-white/[0.06] border-[#0a0a0a] text-white/40' : 'bg-slate-100 border-white text-slate-400'} ${m.role === 'admin' ? `ring-1 ${darkMode ? 'ring-white/20' : 'ring-slate-300'}` : ''}`}
                    title={`${m.username}${m.role === 'admin' ? ' (Admin)' : ''}`}
                  >
                    {m.username[0].toUpperCase()}
                  </div>
                ))}
              </div>
              <p className={`text-[10px] ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>{activeGroup.members?.length} members · {myRole === 'admin' ? 'Admin' : 'Member'}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-center flex-wrap">
              <button onClick={() => { setShowInvitePanel(!showInvitePanel); setShowMembersPanel(false); }} className={`px-4 py-2 rounded-lg text-[9px] font-bold uppercase tracking-[0.1em] flex items-center gap-1.5 transition-all active:scale-95 ${darkMode ? 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08] hover:text-white/60' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                <UserPlus size={10} /> Invite
              </button>
              {myRole === 'admin' && (
                <button onClick={() => { setShowMembersPanel(!showMembersPanel); setShowInvitePanel(false); }} className={`px-4 py-2 rounded-lg text-[9px] font-bold uppercase tracking-[0.1em] flex items-center gap-1.5 transition-all active:scale-95 ${darkMode ? 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08] hover:text-white/60' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                  <Users size={10} /> Manage
                </button>
              )}
              <button onClick={leaveGroup} className={`px-4 py-2 rounded-lg text-[9px] font-bold uppercase tracking-[0.1em] flex items-center gap-1.5 transition-all active:scale-95 ${darkMode ? 'bg-white/[0.04] text-rose-400/60 hover:text-rose-400' : 'bg-slate-100 text-rose-400 hover:text-rose-500'}`}>
                <LogOut size={10} /> Leave
              </button>
              {myRole === 'admin' && (
                <button onClick={deleteGroup} className={`px-4 py-2 rounded-lg text-[9px] font-bold uppercase tracking-[0.1em] flex items-center gap-1.5 transition-all active:scale-95 ${darkMode ? 'bg-white/[0.04] text-rose-400/60 hover:text-rose-400' : 'bg-slate-100 text-rose-400 hover:text-rose-500'}`}>
                  <Trash2 size={10} /> Delete
                </button>
              )}
            </div>

            {/* Manage Members Panel */}
            {showMembersPanel && myRole === 'admin' && (
              <div className={`space-y-0 animate-in fade-in`}>
                <input
                  type="text"
                  placeholder="Search members..."
                  className={`w-full p-3.5 rounded-xl text-[13px] outline-none transition-all mb-3 ${darkMode ? 'bg-white/[0.03] text-white/80 border border-white/[0.06] placeholder:text-white/15 focus:border-white/15' : 'bg-slate-50/80 text-slate-700 border border-slate-100 placeholder:text-slate-300 focus:border-slate-200'}`}
                  value={memberSearchQuery}
                  onChange={e => setMemberSearchQuery(e.target.value)}
                />
                {filteredActiveMembers.length === 0 ? (
                  <p className={`text-[13px] text-center py-4 ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>No members found.</p>
                ) : (
                  filteredActiveMembers.map((m, i) => (
                    <div key={m.id} className={`flex items-center justify-between py-3 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}>
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}>{m.username[0].toUpperCase()}</div>
                        <div>
                          <span className={`text-[13px] font-medium ${darkMode ? 'text-white/70' : 'text-slate-600'}`}>{m.username}</span>
                          {m.role === 'admin' && <span className={`ml-2 text-[9px] font-bold uppercase tracking-widest ${darkMode ? 'text-[#d4af37]/60' : 'text-[#d4af37]'}`}>Admin</span>}
                        </div>
                      </div>
                      {m.role !== 'admin' && m.id !== currentUser.id && (
                        <div className="flex gap-1.5">
                          <button onClick={() => makeGroupAdmin(m.id)} className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-widest transition-all ${darkMode ? 'bg-white/[0.06] text-white/40 hover:text-[#d4af37]' : 'bg-slate-100 text-slate-400 hover:text-[#d4af37]'}`}>Admin</button>
                          <button onClick={() => removeGroupMember(m.id)} className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-widest transition-all ${darkMode ? 'bg-white/[0.06] text-white/40 hover:text-rose-400' : 'bg-slate-100 text-slate-400 hover:text-rose-500'}`}>Remove</button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Invite Panel */}
            {showInvitePanel && (
              <div className={`space-y-0 animate-in fade-in ${darkMode ? '' : ''}`}>
                <input
                  type="text"
                  placeholder="Search friends..."
                  className={`w-full p-3.5 rounded-xl text-[13px] outline-none transition-all mb-3 ${darkMode ? 'bg-white/[0.03] text-white/80 border border-white/[0.06] placeholder:text-white/15 focus:border-white/15' : 'bg-slate-50/80 text-slate-700 border border-slate-100 placeholder:text-slate-300 focus:border-slate-200'}`}
                  value={inviteFriendSearch}
                  onChange={e => setInviteFriendSearch(e.target.value)}
                />
                {invitableFriends.length === 0 ? (
                  <p className={`text-[13px] text-center py-4 ${darkMode ? 'text-white/15' : 'text-slate-300'}`}>No friends to invite</p>
                ) : (
                  invitableFriends.slice(0, 5).map((f, i) => (
                    <div key={f.id} className={`flex items-center justify-between py-3 ${i > 0 ? `border-t ${darkMode ? 'border-white/[0.04]' : 'border-slate-100'}` : ''}`}>
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${darkMode ? 'bg-white/[0.06] text-white/40' : 'bg-slate-100 text-slate-400'}`}>{f.username[0].toUpperCase()}</div>
                        <span className={`text-[13px] font-medium ${darkMode ? 'text-white/70' : 'text-slate-600'}`}>{f.username}</span>
                      </div>
                      {groupOutgoingInvites.includes(f.id) ? (
                        <span className={`text-[9px] font-bold uppercase tracking-widest ${darkMode ? 'text-white/20' : 'text-slate-300'}`}>Sent</span>
                      ) : (
                        <button onClick={() => sendGroupInvite(f.id)} className={`p-2 rounded-lg transition-all active:scale-90 ${darkMode ? 'bg-white text-black' : 'bg-[#111] text-white'}`}>
                          <Send size={11} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Group Tabs: Leaderboard / Quests */}
            <div className={`flex p-1 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
              <button onClick={() => setGroupTab('quests')} className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${groupTab === 'quests' ? `${darkMode ? 'bg-white/10 text-white' : 'bg-white shadow text-[#064e3b]'}` : 'text-slate-400'}`}>Quests</button>
              <button onClick={() => setGroupTab('leaderboard')} className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${groupTab === 'leaderboard' ? `${darkMode ? 'bg-white/10 text-white' : 'bg-white shadow text-[#064e3b]'}` : 'text-slate-400'}`}>Leaderboard</button>
            </div>

            {/* GROUP LEADERBOARD */}
            {groupTab === 'leaderboard' && (
              <div className="space-y-2">
                {activeGroup.members
                  ?.sort((a, b) => b.xp - a.xp)
                  .map((m, i) => (
                    <div key={m.id} className={`flex items-center gap-4 p-4 rounded-2xl transition-all ${i === 0 ? (darkMode ? 'bg-[#d4af37]/10 border border-[#d4af37]/20' : 'bg-[#fffbeb] border border-[#d4af37]/20') : (darkMode ? 'bg-white/5' : 'bg-white shadow-sm')}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-[#d4af37] text-white' : i === 1 ? 'bg-slate-300 text-white' : i === 2 ? 'bg-amber-700 text-white' : (darkMode ? 'bg-white/10 text-white/40' : 'bg-slate-100 text-slate-400')}`}>
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <p className={`font-bold text-sm ${darkMode ? 'text-white' : ''}`}>
                          {m.username}
                          {m.role === 'admin' && <span className="text-[#d4af37] ml-1">⭐</span>}
                          {m.id === currentUser.id && <span className="text-[10px] ml-2 text-slate-400">(You)</span>}
                        </p>
                      </div>
                      <p className={`font-bold text-sm ${darkMode ? 'text-[#d4af37]' : 'text-[#064e3b]'}`}>{m.xp.toLocaleString()} XP</p>
                      {myRole === 'admin' && m.id !== currentUser.id && (
                        <div className="flex gap-1">
                          {m.role !== 'admin' && (
                            <button onClick={() => promoteMember(m.id)} title="Make Admin" className="p-1 rounded text-slate-400 hover:text-[#d4af37]"><Crown size={14} /></button>
                          )}
                          <button onClick={() => removeMember(m.id)} title="Remove" className="p-1 rounded text-slate-400 hover:text-rose-500"><X size={14} /></button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}

            {/* GROUP CHALLENGES */}
            {groupTab === 'quests' && (
              <div className="space-y-6">
                {/* Admin: Create Challenge */}
                {myRole === 'admin' && (
                  <div className={`p-4 rounded-2xl space-y-3 ${darkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                    <h3 className="text-xs font-bold uppercase tracking-wider opacity-60">Create Group Challenge</h3>
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Challenge Title (e.g. Read Surah Kahf)"
                        className={`w-full p-3 rounded-xl text-sm outline-none ${darkMode ? 'bg-black/20 text-white placeholder-white/30' : 'bg-white text-slate-900'}`}
                        value={newGroupQuestTitle}
                        onChange={e => setNewGroupQuestTitle(e.target.value)}
                        maxLength={100}
                      />
                      <input
                        type="date"
                        value={newGroupQuestDeadline}
                        onChange={(e) => setNewGroupQuestDeadline(e.target.value)}
                        className={`w-full p-3 rounded-xl outline-none text-sm ${darkMode ? 'bg-black/20 text-white' : 'bg-white text-slate-900'}`}
                      />
                      <button
                        onClick={addGroupQuest}
                        disabled={!newGroupQuestTitle.trim()}
                        className="w-full py-3 bg-[#d4af37] text-white rounded-xl font-bold text-xs uppercase tracking-widest disabled:opacity-50"
                      >
                        Create Challenge (50 XP)
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {groupQuests.filter(q => !hiddenQuests.includes(q.id)).length > 0 && <h4 className={`text-[10px] font-black uppercase tracking-widest pl-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>Active Challenges</h4>}
                  {groupQuests.filter(q => !hiddenQuests.includes(q.id)).map(quest => {
                    const trackingId = `gq_${quest.id}`;
                    const isTracking = currentUser.activeQuests?.includes(trackingId);
                    const completedCount = groupQuestCompletions[quest.id] || 0;
                    const totalMembers = activeGroup.members?.length || 0;
                    const isLocked = completedCount < totalMembers;
                    const iUserCompleted = currentUser.completedDailyQuests?.[trackingId] || false;
                    const isFullyComplete = !isLocked; // All members completed
                    const isDeclined = declinedQuests.includes(quest.id);

                    // Calculations for Greyed Out
                    // Gray out if: User completed BUT group not finished
                    const isGreyedOut = iUserCompleted && !isFullyComplete;

                    return (
                      <div key={quest.id} className={`p-4 rounded-2xl border flex flex-col gap-3 transition-all duration-200 ${isGreyedOut ? 'opacity-50 grayscale' : ''} ${darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${darkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>Group Challenge</span>
                              {quest.deadline && <span className="text-[9px] font-bold text-rose-400 flex items-center gap-1"><Clock size={10} /> Ends {new Date(quest.deadline).toLocaleDateString()}</span>}
                            </div>
                            <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{quest.title}</h4>
                            {isGreyedOut && <p className="text-[10px] text-slate-400 font-bold mt-1">Waiting for other members to complete...</p>}
                            {isFullyComplete && <p className="text-[10px] text-emerald-500 font-bold mt-1">Challenge Completed! All members done.</p>}
                            {isDeclined && <p className="text-[10px] text-rose-400 font-bold mt-1">Declined</p>}
                          </div>

                          {/* Actions */}
                          <div className={`flex items-center gap-2 ${isGreyedOut ? 'pointer-events-none' : ''}`}>

                            {/* Track Button (Show if not completed, not declined, not fully complete) */}
                            {!iUserCompleted && !isFullyComplete && !isDeclined && (
                              <button
                                onClick={() => onTrackQuest?.({
                                  id: trackingId,
                                  title: quest.title,
                                  description: `Group Challenge • ${quest.xp} XP`,
                                  category: 'COMMUNITY',
                                  xp: quest.xp,
                                  isGroupQuest: true,
                                  groupId: quest.group_id,
                                  deadline: quest.deadline
                                } as any)}
                                className={`px-3 py-1.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all duration-200 ${isTracking
                                  ? 'bg-slate-200 text-slate-500 hover:bg-slate-300'
                                  : 'bg-orange-500 text-white hover:bg-orange-600'}`}
                                title={isTracking ? "Untrack" : "Track"}
                              >
                                {isTracking ? 'Untrack' : 'Track'}
                              </button>
                            )}

                            {/* Decline Button (Removes from view) */}
                            {!isTracking && !iUserCompleted && !isFullyComplete && !isDeclined && (
                              <button
                                onClick={() => { if (confirm('Decline and remove this challenge?')) handleHideQuest(quest.id); }}
                                className="px-3 py-1.5 rounded-xl bg-orange-500 text-white font-black text-[10px] uppercase tracking-widest hover:bg-orange-600 transition-all duration-200"
                                style={{ backgroundColor: '#f43f5e' }} // Force Rose-500
                                title="Decline"
                              >
                                Decline
                              </button>
                            )}

                            {/* User Delete (Unlock if Declined OR Fully Complete) */}
                            {(isDeclined || isFullyComplete) && (
                              <button
                                onClick={() => { if (confirm('Remove this card from your view?')) handleHideQuest(quest.id); }}
                                className={`p-2 rounded-xl text-slate-400 hover:text-rose-500 ${darkMode ? 'bg-white/10' : 'bg-slate-100'}`}
                                title="Remove Card"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}

                            {/* Admin Delete (Only for Admin, separate from local hide) */}
                            {myRole === 'admin' && !isFullyComplete && !isDeclined && (
                              <button onClick={async () => {
                                if (confirm('Delete this challenge for EVERYONE?')) {
                                  try {
                                    await supabase.from('group_quest_completions').delete().eq('group_quest_id', quest.id);
                                    const { error } = await supabase.from('group_quests').delete().eq('id', quest.id);
                                    if (error) throw error;
                                    setGroupQuests(prev => prev.filter(q => q.id !== quest.id));
                                  } catch (e) {
                                    console.error('Delete failed:', e);
                                    alert('Could not delete challenge.');
                                  }
                                }
                              }} className="p-2 rounded-xl hover:bg-rose-500/10 text-slate-400 hover:text-rose-500 transition-all duration-200" title="Admin Delete">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Progress Bar (Always Show) */}
                        <div className="text-xs opacity-70">
                          <div className="flex justify-between mb-1 text-[9px] font-bold uppercase tracking-wider">
                            <span>Group Progress</span>
                            <span>{completedCount}/{totalMembers} Members Completed</span>
                          </div>
                          <div className={`h-1.5 w-full rounded-full overflow-hidden ${darkMode ? 'bg-white/10' : 'bg-slate-200'}`}>
                            <div className={`h-full transition-all duration-500 ${isLocked ? 'bg-slate-400' : 'bg-indigo-500'}`} style={{ width: `${(completedCount / totalMembers) * 100}%` }}></div>
                          </div>
                          {isLocked && iUserCompleted && (
                            <div className="flex items-center gap-1 mt-1 text-slate-400 italic text-[10px]">
                              Waiting for others to finish to unlock XP...
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Community;
